import type { Logger } from "@expert/logging";
import type {
    GaiaErrorBody,
    GaiaErrorMessage,
    ProactiveMessagePayload,
    ReactiveMessagePayload,
} from "@expert/shared-types";

import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    type GaiaWebSocketResponse,
    gaiaWsEventBus,
    isProactiveErrorMessage,
    isProactiveMessage,
    isProactiveRecommendationMessage,
    isReactiveErrorMessage,
    isReactiveMessage,
} from "@expert/gaia";
import { triggerProactiveSound, triggerTabNotification } from "@expert/proactive-notifications";
import { addFailureMessage, handleIncomingMessage } from "@soluto-private/expert-workspace-timeline";
import { useEffect, useMemo } from "react";

import { handleGuidedFlowRecommendation } from "../../utils/";

export const useGaiaMessageEvents = (loggerProp: Logger) => {
    const {
        features: { isGuidedFlowRecommendationEnabled, isProactiveEnabled },
    } = useFeatures<TimelineFeatures>();
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaMessageEvents" }), [loggerProp]);

    useEffect(() => {
        // Reactive Message Handling
        const gaiaEventBusMessageSuccess = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-success",
            ({ body }: GaiaWebSocketResponse) => {
                /*
                    The orchestrator invoke success event returns reactive,
                    proactive, and autocomplete messages. Both proactive and
                    reactive messages are being handled in this event handler.
                    Auto complete messages are being handled in the useGaiaAutoCompleteEvents
                    handler.
                */
                if (!isReactiveMessage(body as ReactiveMessagePayload)) return;
                handleIncomingMessage(body as ReactiveMessagePayload, logger);
            },
        );

        // Proactive Message Handling
        const gaiaEventBusProactiveMessageReceived = gaiaWsEventBus.on(
            "gaia_ws_message-suggestion",
            ({ body }: GaiaWebSocketResponse) => {
                const gaiaSuggestion = body as unknown as ProactiveMessagePayload;

                if (isGuidedFlowRecommendationEnabled && isProactiveRecommendationMessage(gaiaSuggestion)) {
                    handleGuidedFlowRecommendation(gaiaSuggestion, logger);
                    return;
                }

                // All messages are coming through this event (reactive messages + proactive messages)
                if (!isProactiveEnabled || !isProactiveMessage(gaiaSuggestion)) return;

                // TODO: error messages should not return with messageType = proactive
                // working with gaia team to resolve this
                if (isProactiveErrorMessage(gaiaSuggestion)) {
                    logger.warn(
                        {
                            originalError: gaiaSuggestion.suggestion.message,
                        },
                        "GAIA | websocket proactive error message response",
                    );
                    return;
                }

                handleIncomingMessage(gaiaSuggestion, logger);
                triggerTabNotification("Expert Workspace", isProactiveEnabled);
                triggerProactiveSound(isProactiveEnabled);
            },
        );

        const gaiaEventBusOrchestratorFailure = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-error",
            ({ body }: GaiaWebSocketResponse) => {
                const errorMessage = body as GaiaErrorMessage;

                let errorBody: GaiaErrorBody;
                try {
                    errorBody = JSON.parse(errorMessage.body) as GaiaErrorBody;
                } catch {
                    logger.error({ errorMessage }, "GAIA | Failed to parse error body");
                    return;
                }

                if (isReactiveErrorMessage(errorBody)) {
                    addFailureMessage(errorBody.sessionGroupId, "botFailure");
                }

                logger.warn(
                    {
                        errorMessage,
                        errorBody,
                    },
                    "GAIA | websocket error message response",
                );
            },
        );

        const gaiaEventBusSessionContextFailure = gaiaWsEventBus.on(
            "gaia_ws_update-session-context-error",
            ({ body }: GaiaWebSocketResponse) => {
                const { ...errorMessage } = body as GaiaErrorMessage;
                const { ...errorBody } = JSON.parse(errorMessage.body) as unknown as GaiaErrorBody;

                logger.warn(
                    {
                        errorMessage,
                        errorBody,
                    },
                    "GAIA | update session context error message response",
                );
            },
        );

        return () => {
            gaiaEventBusMessageSuccess();
            gaiaEventBusOrchestratorFailure();
            gaiaEventBusProactiveMessageReceived();
            gaiaEventBusSessionContextFailure();
        };
    }, [isGuidedFlowRecommendationEnabled, isProactiveEnabled, logger]);
};
